import * as CL from '@design-system/component-library';
import { useLocation, useNavigate, useNavigation } from 'react-router';
import { dsClass } from '../../common/constants/dsClasses.js';
import { paths } from '../../common/constants/pathVariables.js';
import {
  changeFeeMsg,
  continueMsg,
  formatNumber,
  monthMsg,
  quantityMsg,
  t,
  totalMsg,
} from '../../common/i18n/index.js';
import { SalesType } from '../../generated/api/models.js';
import { Grid } from '../Grid/Grid';
import { getFormattedPricesWithAddons } from './OrderSubscriptionSelectionUtils';
import { SUBSCRIPTION_CHANGE_SERVICE_FEE } from './content/LaitenettiSubscriptionCardContent';
import type { AddedAddon } from '../../common/types/addon.js';
import type { CampaignAssociation, CommercialProduct, Offer, Subscription } from '../../generated/api/models.js';
import type { SubscriptionUpdateNavigationState } from '../../public/site/path/SelfService/ProductsAndServices/MobileSubUpdateConfirmationPath.js';

interface OrderPriceProps {
  amount: number;
  selectedAddOns: AddedAddon[];
  isChangeOffer: boolean;
  selectedCampaignAssociation?: CampaignAssociation;
  selectedCommercialProduct?: CommercialProduct;
  campaignDescription?: string;
  selectedServiceFee?: { price: number; productId: string };
  useBundledAddOnPricing?: boolean;
}

const OrderPrice = (props: OrderPriceProps) => {
  const {
    selectedCommercialProduct,
    amount,
    selectedAddOns,
    selectedCampaignAssociation,
    isChangeOffer,
    selectedServiceFee = { price: SUBSCRIPTION_CHANGE_SERVICE_FEE },
    useBundledAddOnPricing,
  } = props;
  if (!selectedCommercialProduct) {
    return null;
  }

  const { oneTimePrice, recurringPrice } = getFormattedPricesWithAddons(
    selectedCommercialProduct,
    amount,
    selectedAddOns,
    selectedCampaignAssociation,
    SalesType.NEW_SALE,
    useBundledAddOnPricing
  );

  return (
    <div className="of-order-price">
      <span className="ea-h3 ea-h3--thick">
        {t.CEQ2(totalMsg)}: {recurringPrice}/{t.XXVX(monthMsg)}{' '}
      </span>
      <span className="ea-h4">{t.JZ8R('(VAT 0%)')}</span>
      <br />
      {isChangeOffer
        ? `${t.ZRKX(changeFeeMsg)} ${formatNumber(selectedServiceFee.price / 100)} €`
        : `${t.Y7WJ('Activation fees')}: ${oneTimePrice}`}
    </div>
  );
};

type SubscriptionSelectionFooterProps = OrderPriceProps &
  (
    | {
        isChangeOffer: true;
        isProductSelected: boolean;
        selectedCommercialProduct?: CommercialProduct;
        subscription?: Subscription;
        onlineModelName?: string;
        offer?: Offer;
        previousOffer?: Offer;
        voucher?: string;
      }
    | {
        isChangeOffer: false;
        isProductSelected: boolean;
        handleSubmit: () => void;
        handleAmountChanged: (value: number) => void;
      }
  );
export const SubscriptionSelectionFooter = (props: SubscriptionSelectionFooterProps) => {
  const {
    isProductSelected,
    isChangeOffer,
    amount,
    selectedCommercialProduct,
    selectedAddOns,
    selectedCampaignAssociation,
    campaignDescription,
    selectedServiceFee,
  } = props;
  const { state } = useNavigation();
  const navigate = useNavigate();
  const { search } = useLocation();

  const getButtonText = () => {
    if (isChangeOffer) {
      return t.I62A(continueMsg);
    }
    return t.B5J1('Add to basket');
  };

  return (
    <div className={`${isProductSelected ? '' : dsClass.VISIBILITY_HIDDEN}`}>
      <Grid>
        <CL.Grid>
          <CL.GridRow justifyCenter>
            <CL.GridCol colWidthXS={4} colWidthS={6} colWidthM={3} colWidthL={5}>
              <OrderPrice {...props} />
            </CL.GridCol>
            <CL.GridCol colWidthXS={4} colWidthS={6} colWidthM={3} colWidthL={5} className={dsClass.TEXT_ALIGN_RIGHT}>
              <form className="sticky-footer-form">
                {!isChangeOffer && (
                  <>
                    <label>{t.M0W7(quantityMsg)}</label>
                    <CL.Quantity
                      currentValue={amount}
                      maxValue={99}
                      minValue={1}
                      onChange={value => props.handleAmountChanged(value)}
                    />
                  </>
                )}
                <CL.Button
                  size="l"
                  loading={state === 'loading'}
                  onClick={e => {
                    e.stopPropagation();
                    e.preventDefault();
                    isChangeOffer
                      ? navigate(paths.PS_MOBILE_SUBSCRIPTION_UPDATE_CONFIRMATION + search, {
                          state: {
                            selectedCommercialProduct: { ...selectedCommercialProduct, oneTimeCharge: 0 },
                            subscription: props.subscription,
                            selectedAddOns,
                            campaignAssociation: selectedCampaignAssociation,
                            campaignDescription: campaignDescription,
                            selectedServiceFee,
                            onlineModelName: props.onlineModelName,
                            offer: props.offer,
                            previousOffer: props.previousOffer,
                            voucher: props.voucher,
                          } as SubscriptionUpdateNavigationState,
                        })
                      : props.handleSubmit();
                  }}
                >
                  {getButtonText()}
                </CL.Button>
              </form>
            </CL.GridCol>
          </CL.GridRow>
        </CL.Grid>
      </Grid>
    </div>
  );
};
